/**
 * Inbox folders list-surface continuity prototype
 * Access: /?test=inbox-folders
 *
 * Uses Flows Studio chrome framework: MaaterProtoShell + ProtoAppHeader +
 * ProtoIconRail + ProtoModePanel (folders) + ProtoTabRow + ProtoTitleRow.
 * Secondary preview sits BELOW the title row, beside the list.
 *
 * Guide: docs/design/maater-list-surface-continuity.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Archive,
  Bell,
  Clock,
  Eye,
  Folder,
  Inbox,
  Mail,
  MessageSquare,
  Mic,
  Moon,
  MoreHorizontal,
  PanelRight,
  Puzzle,
  Reply,
  Sparkles,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import {
  MaaterProtoShell,
  type MaaterProtoThemeId,
} from '@/components/prototype/maater-proto-shell'
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoModePanel,
  ProtoTabRow,
  ProtoTitleRow,
  type ProtoCrumb,
} from '@/components/prototype/maater-proto-chrome'

type FolderId =
  | 'inbox'
  | 'unread'
  | 'flows'
  | 'chats'
  | 'suggestions'
  | 'voice'
  | 'email'
  | 'extensions'
  | 'snoozed'
  | 'archived'
  // Socialization only (ADR-0021). Eventual home is a per-workspace surface / icon-bar noun - not a permanent Inbox folder.
  | 'team-activity'
type ThreadKind = 'flow' | 'chat' | 'suggestion' | 'voice' | 'email' | 'extension'
type WorkspaceId = 'hop' | 'personal'
type Scene = 'list' | 'reader'

interface Thread {
  id: string
  title: string
  kind: ThreadKind
  snippet: string
  workspace: WorkspaceId
  relativeTime: string
  unread: boolean
  folderIds: FolderId[]
  body: string
}

interface HistoryEntry {
  scene: Scene
  folderId: FolderId
  threadId: string | null
}

const FOLDER_META: { id: FolderId; label: string; icon: LucideIcon; hideIfEmpty?: boolean }[] = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'unread', label: 'Unread', icon: Bell },
  { id: 'flows', label: 'Flows', icon: Workflow },
  { id: 'chats', label: 'Chats', icon: MessageSquare },
  { id: 'suggestions', label: 'Suggestions', icon: Sparkles },
  { id: 'voice', label: 'Voice', icon: Mic },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'extensions', label: 'Extensions', icon: Puzzle, hideIfEmpty: true },
  { id: 'snoozed', label: 'Snoozed', icon: Moon },
  { id: 'archived', label: 'Archived', icon: Archive },
  // Teach-only: workspace history / webhooks live here later - not personal Inbox.
  { id: 'team-activity', label: 'Team Activity (soon)', icon: Users },
]

const KIND_LABEL: Record<ThreadKind, string> = {
  flow: 'Flow',
  chat: 'Chat',
  suggestion: 'Suggestion',
  voice: 'Voice',
  email: 'Email',
  extension: 'Extension',
}

const WS_LABEL: Record<WorkspaceId, string> = {
  hop: 'Hop Studios',
  personal: 'Personal',
}

/** Compact mock rows: id, title, kind, snippet, ws, time, unread, folders, body */
const THREADS: Thread[] = (
  [
    ['t1', 'Basecamp ingest failed overnight', 'flow', 'Run #482 webhook timeout for GGIA todos.', 'hop', '12m', true, ['inbox', 'unread', 'flows'], 'Flow run #482 failed after three retries. Last success yesterday 11:02 PM.'],
    ['t2', 'Soul proposal: client greeting tone', 'suggestion', 'Soften default client email openers.', 'hop', '38m', true, ['inbox', 'unread', 'suggestions'], 'Prefer warmer, shorter openers on client drafts. Diff ready for approve/reject.'],
    ['t3', 'Planning next sprint with Ops', 'chat', 'Park dual-rail inbox; ship folders first.', 'hop', '1h', false, ['inbox', 'chats'], 'Mode panel = folders only; main = dense list; PanelRight preview; reader is a full scene.'],
    ['t4', 'Voice memo - driveway ideas', 'voice', 'Continuity chrome across Search and Inbox...', 'personal', '2h', true, ['inbox', 'unread', 'voice'], 'Breadcrumbs on every index, no stranded readers, Activity as a Search view.'],
    ['t5', 'Re: Pilot access for Travis', 'email', 'Thursday works if we screen-share folders.', 'hop', '3h', false, ['inbox', 'email'], 'Prefer walking the folders rail vs the old split inbox for the continuity story.'],
    ['t6', 'Google Drive sync quiet', 'extension', 'Drive limb: 0 conflicts, last sync 4m ago.', 'hop', '4h', false, ['inbox', 'extensions'], 'Materialize completed without dirty buffers. No action needed.'],
    ['t7', 'Weekly todo triage reminder', 'flow', 'Opens overdue Hop-only triage view.', 'hop', '1d', false, ['inbox', 'flows', 'snoozed'], 'Snoozed until Monday. Weekly triage skill with Hop-only overdue filter.'],
    ['t8', 'Personal journal prompt', 'chat', 'What felt heavy that Maater already holds?', 'personal', '1d', false, ['inbox', 'chats'], 'Soft check-in chat. Reflection only - no work todos.'],
    ['t9', 'Asana House Sale section sync', 'extension', '3 tasks updated; linked specs current.', 'personal', '2d', false, ['inbox', 'extensions'], 'Asana limb section changes. Linked docs already match titles and GIDs.'],
    ['t10', 'Archived: old dual-rail notes', 'chat', 'inbox-split feedback - superseded.', 'hop', '1w', false, ['archived', 'chats'], 'Kept for history. Reject two columns that both list threads.'],
    ['t11', 'Unread: rename field chips', 'suggestion', 'Date chip when title lacks YYYY-MM-DD.', 'hop', '2d', true, ['unread', 'suggestions'], 'Suggestion from rename-field sessions. Lives under Unread + Suggestions.'],
  ] as const
).map(([id, title, kind, snippet, workspace, relativeTime, unread, folderIds, body]) => ({
  id,
  title,
  kind,
  snippet,
  workspace,
  relativeTime,
  unread,
  folderIds: [...folderIds] as FolderId[],
  body,
}))

function folderLabel(id: FolderId) {
  return FOLDER_META.find((f) => f.id === id)?.label ?? id
}

function countForFolder(folderId: FolderId) {
  return THREADS.filter((t) => t.folderIds.includes(folderId)).length
}

function InboxFoldersTest() {
  const ids = testIds.inboxFoldersPrototype
  const [theme, setTheme] = useState<MaaterProtoThemeId>('paper')
  const [folderId, setFolderId] = useState<FolderId>('inbox')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [scene, setScene] = useState<Scene>('list')
  const [groupByWorkspace, setGroupByWorkspace] = useState(true)
  const [sortNewest, setSortNewest] = useState(true)
  const [history, setHistory] = useState<HistoryEntry[]>([
    { scene: 'list', folderId: 'inbox', threadId: null },
  ])
  const [historyIndex, setHistoryIndex] = useState(0)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    thread: Thread
  } | null>(null)

  const unreadCount = useMemo(
    () => THREADS.filter((t) => t.unread && t.folderIds.includes('inbox')).length,
    [],
  )

  const threadsInFolder = useMemo(() => {
    const list = THREADS.filter((t) => t.folderIds.includes(folderId))
    return sortNewest ? list : [...list].reverse()
  }, [folderId, sortNewest])

  const selected = THREADS.find((t) => t.id === selectedId) ?? null
  const readerThread =
    scene === 'reader' && selectedId
      ? THREADS.find((t) => t.id === selectedId) ?? null
      : null

  const pushHistory = useCallback(
    (entry: HistoryEntry) => {
      setHistory((prev) => [...prev.slice(0, historyIndex + 1), entry])
      setHistoryIndex((i) => i + 1)
    },
    [historyIndex],
  )

  const applyEntry = (entry: HistoryEntry) => {
    setScene(entry.scene)
    setFolderId(entry.folderId)
    setSelectedId(entry.threadId)
  }

  const goBack = () => {
    if (historyIndex <= 0) return
    const next = historyIndex - 1
    setHistoryIndex(next)
    applyEntry(history[next])
  }

  const goForward = () => {
    if (historyIndex >= history.length - 1) return
    const next = historyIndex + 1
    setHistoryIndex(next)
    applyEntry(history[next])
  }

  const selectFolder = (id: FolderId) => {
    setFolderId(id)
    setScene('list')
    setSelectedId(null)
    pushHistory({ scene: 'list', folderId: id, threadId: null })
  }

  const openReader = useCallback(
    (thread: Thread) => {
      setSelectedId(thread.id)
      setScene('reader')
      pushHistory({ scene: 'reader', folderId, threadId: thread.id })
    },
    [folderId, pushHistory],
  )

  const backToList = () => {
    setScene('list')
    pushHistory({ scene: 'list', folderId, threadId: selectedId })
  }

  const restoreListRoot = () => {
    setScene('list')
    setFolderId('inbox')
    setSelectedId(null)
    pushHistory({ scene: 'list', folderId: 'inbox', threadId: null })
  }

  const onCrumb = (id: string) => {
    if (id === 'inbox-root') restoreListRoot()
    else if (id === 'folder') backToList()
  }

  useEffect(() => {
    if (!contextMenu) return
    const close = () => setContextMenu(null)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [contextMenu])

  useEffect(() => {
    if (scene !== 'list') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !selectedId) return
      const target = e.target as HTMLElement | null
      if (target?.closest('textarea, input, [contenteditable]')) return
      const t = THREADS.find((x) => x.id === selectedId)
      if (t) openReader(t)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [scene, selectedId, openReader])

  const visibleFolders = FOLDER_META.filter(
    (f) => !f.hideIfEmpty || countForFolder(f.id) > 0,
  )

  const grouped = useMemo(() => {
    if (!groupByWorkspace) {
      return [{ key: 'all', label: null as string | null, threads: threadsInFolder }]
    }
    return (['hop', 'personal'] as WorkspaceId[])
      .map((ws) => ({
        key: ws,
        label: WS_LABEL[ws],
        threads: threadsInFolder.filter((t) => t.workspace === ws),
      }))
      .filter((g) => g.threads.length > 0)
  }, [groupByWorkspace, threadsInFolder])

  const crumbs: ProtoCrumb[] = useMemo(() => {
    const base: ProtoCrumb[] = [{ id: 'inbox-root', label: 'Inbox' }]
    if (folderId !== 'inbox') {
      base.push({ id: 'folder', label: folderLabel(folderId) })
    }
    if (scene === 'reader' && readerThread) {
      base.push({ id: 'thread', label: readerThread.title })
    } else if (folderId === 'inbox') {
      // keep single Inbox crumb on list root
    }
    return base
  }, [folderId, scene, readerThread])

  const title =
    scene === 'reader' && readerThread
      ? readerThread.title
      : folderId === 'inbox'
        ? 'Inbox'
        : folderLabel(folderId)

  const subtitle =
    scene === 'reader' && readerThread
      ? `${KIND_LABEL[readerThread.kind]} · ${WS_LABEL[readerThread.workspace]} · ${readerThread.relativeTime}`
      : `${threadsInFolder.length} thread${threadsInFolder.length === 1 ? '' : 's'} · click select · dblclick open`

  const tabs =
    scene === 'reader' && readerThread
      ? [
          { id: 'list', label: folderLabel(folderId) },
          { id: 'reader', label: readerThread.title },
        ]
      : [{ id: 'list', label: folderLabel(folderId) }]

  const activeTabId = scene === 'reader' ? 'reader' : 'list'

  return (
    <MaaterProtoShell
      theme={theme}
      className="flex h-screen min-h-screen flex-col"
      testId={ids.root}
    >
      {contextMenu && (
        <div
          className="fixed z-[200] min-w-[180px] rounded-md border py-1 text-sm shadow-md"
          style={{
            left: Math.min(contextMenu.x, window.innerWidth - 196),
            top: Math.min(contextMenu.y, window.innerHeight - 100),
            borderColor: 'var(--line)',
            background: 'var(--cream-card)',
            color: 'var(--ink)',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:opacity-90"
            style={{ color: 'var(--ink)' }}
            onClick={() => {
              openReader(contextMenu.thread)
              setContextMenu(null)
            }}
          >
            <Folder className="h-3.5 w-3.5" style={{ color: 'var(--ink-soft)' }} />
            Open
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:opacity-90"
            style={{ color: 'var(--ink)' }}
            onClick={() => {
              setSelectedId(contextMenu.thread.id)
              setPreviewOpen(true)
              setContextMenu(null)
            }}
          >
            <Eye className="h-3.5 w-3.5" style={{ color: 'var(--ink-soft)' }} />
            Preview
          </button>
        </div>
      )}

      <ProtoAppHeader
        theme={theme}
        onTheme={setTheme}
        badge="folders"
        prototypeIndexTestId={ids.prototypeIndex}
        testId={ids.appHeader}
      />

      <div className="flex min-h-0 flex-1">
        <ProtoIconRail
          active="inbox"
          inboxBadge={unreadCount}
          testId={ids.iconRail}
        />

        <ProtoModePanel title="Folders" testId={ids.foldersRail}>
          <nav className="space-y-0.5">
            {visibleFolders.map((f) => {
              const Icon = f.icon
              const active = folderId === f.id && scene === 'list'
              const softActive = folderId === f.id && scene === 'reader'
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => selectFolder(f.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
                  style={
                    active
                      ? {
                          background: 'var(--rust-pale)',
                          color: 'var(--rust)',
                          fontWeight: 600,
                          borderLeft: '2px solid var(--rust)',
                        }
                      : softActive
                        ? {
                            background: 'var(--paper)',
                            color: 'var(--ink)',
                            borderLeft: '2px solid var(--line)',
                          }
                        : {
                            color: 'var(--ink-soft)',
                            borderLeft: '2px solid transparent',
                          }
                  }
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{f.label}</span>
                  <span
                    className="text-[10px] tabular-nums"
                    style={{ color: 'var(--ink-faint)' }}
                  >
                    {countForFolder(f.id)}
                  </span>
                </button>
              )
            })}
          </nav>
        </ProtoModePanel>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ProtoTabRow
            tabs={tabs}
            activeId={activeTabId}
            onSelect={(id) => {
              if (id === 'list') {
                if (scene === 'reader') backToList()
              }
            }}
            testId={ids.tabRow}
          />

          <ProtoTitleRow
            crumbs={crumbs}
            onCrumb={onCrumb}
            title={title}
            subtitle={scene === 'list' ? subtitle : undefined}
            density={scene === 'reader' ? 'inline' : 'stacked'}
            canGoBack={historyIndex > 0}
            canGoForward={historyIndex < history.length - 1}
            onBack={goBack}
            onForward={goForward}
            testId={ids.titleRow}
            backTestId={ids.navBack}
            forwardTestId={ids.navForward}
            actions={
              scene === 'list' ? (
                <>
                  <label
                    className="hidden items-center gap-1.5 text-[11px] sm:flex"
                    style={{ color: 'var(--ink-soft)' }}
                  >
                    <input
                      type="checkbox"
                      checked={groupByWorkspace}
                      onChange={(e) => setGroupByWorkspace(e.target.checked)}
                      className="rounded"
                    />
                    Group
                  </label>
                  <button
                    type="button"
                    onClick={() => setSortNewest((v) => !v)}
                    className="rounded-md px-2 py-1 text-[11px]"
                    style={{
                      border: '1px solid var(--line)',
                      color: 'var(--ink-soft)',
                    }}
                  >
                    {sortNewest ? 'Newest' : 'Oldest'}
                  </button>
                  <button
                    type="button"
                    data-testid={ids.previewToggle}
                    onClick={() => setPreviewOpen((v) => !v)}
                    className="flex h-8 w-8 items-center justify-center rounded-md"
                    style={
                      previewOpen
                        ? {
                            background: 'var(--cream-card)',
                            border: '1.5px solid var(--rust)',
                            color: 'var(--rust)',
                          }
                        : {
                            border: '1.5px solid transparent',
                            color: 'var(--ink-soft)',
                          }
                    }
                    title={previewOpen ? 'Hide preview' : 'Show preview'}
                  >
                    <PanelRight className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    data-testid={ids.readerBack}
                    className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs"
                    style={{
                      border: '1px solid var(--line)',
                      color: 'var(--ink-soft)',
                    }}
                    title="Focus reply (mock)"
                  >
                    <Reply className="h-3.5 w-3.5" /> Reply
                  </button>
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-md"
                    style={{ color: 'var(--ink-soft)' }}
                    title="More (mock)"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </>
              )
            }
          />

          {scene === 'reader' && readerThread ? (
            <div
              className="flex min-h-0 flex-1 flex-col"
              data-testid={ids.readerRoot}
              style={{ background: 'var(--paper)' }}
            >
              <div
                className="flex shrink-0 items-center gap-2 border-b px-4 py-2 text-xs"
                style={{ borderColor: 'var(--line)', color: 'var(--ink-soft)' }}
              >
                <span>
                  {KIND_LABEL[readerThread.kind]} · {WS_LABEL[readerThread.workspace]} ·{' '}
                  {readerThread.relativeTime}
                </span>
              </div>
              <div className="min-h-0 flex-1 overflow-auto px-6 py-5">
                <p
                  className="max-w-2xl text-sm leading-relaxed"
                  style={{ color: 'var(--ink)' }}
                >
                  {readerThread.body}
                </p>
                <p
                  className="mt-3 max-w-2xl text-sm"
                  style={{ color: 'var(--ink-soft)' }}
                >
                  {readerThread.snippet}
                </p>
              </div>
              <div
                className="shrink-0 border-t px-4 py-3"
                style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
              >
                <div className="flex items-end gap-2">
                  <textarea
                    rows={2}
                    placeholder="Reply (mock)..."
                    className="min-h-[44px] flex-1 resize-none rounded-md px-3 py-2 text-sm outline-none"
                    style={{
                      border: '1px solid var(--line)',
                      background: 'var(--paper)',
                      color: 'var(--ink)',
                    }}
                  />
                  <button
                    type="button"
                    className="rounded-md px-3 py-2 text-sm font-medium text-white"
                    style={{ background: 'var(--rust)' }}
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1">
              <div
                className="flex min-w-0 flex-1 flex-col"
                data-testid={ids.threadList}
                style={{ background: 'var(--paper)' }}
              >
                <div className="min-h-0 flex-1 overflow-auto">
                  {threadsInFolder.length === 0 ? (
                    <div
                      className="flex flex-col items-center justify-center gap-2 py-20"
                      style={{ color: 'var(--ink-faint)' }}
                    >
                      <Inbox className="h-8 w-8 opacity-40" />
                      <p className="text-sm">No threads in {folderLabel(folderId)}</p>
                    </div>
                  ) : (
                    grouped.map((group) => (
                      <div key={group.key}>
                        {group.label && (
                          <div
                            className="sticky top-0 z-10 border-b px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider"
                            style={{
                              borderColor: 'var(--line)',
                              background: 'var(--paper-raised)',
                              color: 'var(--ink-soft)',
                            }}
                          >
                            {group.label}
                          </div>
                        )}
                        <ul>
                          {group.threads.map((thread) => {
                            const active = selectedId === thread.id
                            return (
                              <li key={thread.id}>
                                <button
                                  type="button"
                                  onClick={() => setSelectedId(thread.id)}
                                  onDoubleClick={() => openReader(thread)}
                                  onContextMenu={(e) => {
                                    e.preventDefault()
                                    setSelectedId(thread.id)
                                    setContextMenu({
                                      x: e.clientX,
                                      y: e.clientY,
                                      thread,
                                    })
                                  }}
                                  className={cn(
                                    'flex w-full items-start gap-3 border-b px-3 py-2.5 text-left',
                                  )}
                                  style={{
                                    borderColor: 'var(--line)',
                                    borderLeftWidth: 2,
                                    borderLeftStyle: 'solid',
                                    borderLeftColor: active
                                      ? 'var(--rust)'
                                      : 'transparent',
                                    background: active
                                      ? 'var(--rust-pale)'
                                      : thread.unread
                                        ? 'color-mix(in oklch, var(--paper-raised) 80%, transparent)'
                                        : 'transparent',
                                    color: 'var(--ink)',
                                  }}
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline gap-2">
                                      <span
                                        className={cn(
                                          'truncate text-sm',
                                          thread.unread ? 'font-semibold' : 'font-medium',
                                        )}
                                      >
                                        {thread.title}
                                      </span>
                                      <span
                                        className="shrink-0 text-[10px]"
                                        style={{ color: 'var(--ink-faint)' }}
                                      >
                                        {thread.relativeTime}
                                      </span>
                                    </div>
                                    <p
                                      className="mt-0.5 truncate text-xs"
                                      style={{ color: 'var(--ink-soft)' }}
                                    >
                                      {KIND_LABEL[thread.kind]} · {thread.snippet}
                                    </p>
                                    <p
                                      className="mt-0.5 flex items-center gap-1 text-[10px]"
                                      style={{ color: 'var(--ink-faint)' }}
                                    >
                                      <Clock className="h-2.5 w-2.5" />
                                      {WS_LABEL[thread.workspace]}
                                    </p>
                                  </div>
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    ))
                  )}
                </div>
                <ListSurfaceStatusBar includePreviewTip />
              </div>

              {previewOpen && (
                <aside
                  className="flex w-[min(320px,38%)] shrink-0 flex-col border-l"
                  style={{
                    borderColor: 'var(--line)',
                    background: 'var(--paper-raised)',
                  }}
                  data-testid={ids.previewPanel}
                >
                  {selected ? (
                    <>
                      <div
                        className="border-b px-3 py-2"
                        style={{ borderColor: 'var(--line)' }}
                      >
                        <p
                          className="text-[10px] uppercase tracking-wide"
                          style={{ color: 'var(--ink-faint)' }}
                        >
                          Preview
                        </p>
                        <h3
                          className="mt-0.5 text-sm font-semibold leading-snug"
                          style={{ color: 'var(--ink)' }}
                        >
                          {selected.title}
                        </h3>
                      </div>
                      <div
                        className="min-h-0 flex-1 overflow-auto px-3 py-3 text-sm"
                        style={{ color: 'var(--ink-soft)' }}
                      >
                        <p style={{ color: 'var(--ink)' }}>{selected.snippet}</p>
                        <p className="mt-3 leading-relaxed">{selected.body}</p>
                      </div>
                      <div
                        className="border-t p-2"
                        style={{ borderColor: 'var(--line)' }}
                      >
                        <button
                          type="button"
                          onClick={() => openReader(selected)}
                          className="w-full rounded-md px-2 py-1.5 text-xs"
                          style={{
                            border: '1px solid var(--line)',
                            color: 'var(--ink)',
                          }}
                        >
                          Open full reader
                        </button>
                      </div>
                    </>
                  ) : (
                    <div
                      className="flex flex-1 items-center justify-center p-6 text-center text-xs"
                      style={{ color: 'var(--ink-faint)' }}
                    >
                      Select a thread, or choose Preview from the row menu
                    </div>
                  )}
                </aside>
              )}
            </div>
          )}
        </main>
      </div>
    </MaaterProtoShell>
  )
}

export { InboxFoldersTest }
export default InboxFoldersTest
